import { IonButton, IonCard, IonCardHeader, IonCardTitle, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonLoading, IonNote, IonPage, IonRow, IonTitle, IonToolbar } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import { passwordChangeMutation } from "../../apollo-client/queries";
import { Redirect, useHistory, useLocation } from "react-router";
import { useTenant } from "../../hooks/multiTenancy";
import queryString from 'query-string';
import { informationCircle, warning } from "ionicons/icons";
import "./auth.css";

type ChangePasswordForm = {
  newPassword: string;
  confirmPassword: string;
};

const PasswordRecovery: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const tenant = useTenant();
  const { search } = useLocation();

  const { otrToken, webOrigin } = queryString.parse(search);
  const isWebOrigin = webOrigin ? ((typeof webOrigin === "string" ? webOrigin : webOrigin[0]) === "true") : false;

  const [passwordReset, { called: passwordChangeCalled, loading: passwordChangeLoading, data: passwordChangeData }] = useMutation(passwordChangeMutation);

  const schema = yup.object().shape({
    newPassword: yup.string().min(6, t("auth.min_six_chars")).required(t("required")),
    confirmPassword: yup.string().equals([yup.ref("newPassword")], t("auth.passwords_must_match")).required(t("required")),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ChangePasswordForm>({
    resolver: yupResolver(schema),
    reValidateMode: "onSubmit",
  });

  const onSubmit = async (data: ChangePasswordForm) => {
    try {
      await passwordReset({variables: { 
        password: data.newPassword, 
        oneTimeRecovery: otrToken 
      }});
      if (isWebOrigin) {
        setTimeout(() => {
          history.push("/");
        }, 2000);
      }
    } catch (e) {
      console.log(e);
    }
  };

  if (!otrToken || typeof otrToken !== "string") {
    return <Redirect to="/" />;
  }

  return (
    <IonPage className="auth">
      <IonLoading isOpen={passwordChangeLoading} message={t("loading")} />
      <IonHeader>
        <IonToolbar color="secondary">
          <IonTitle>{t("auth.change_password")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div className="background-image" style={{ backgroundImage: `url('${tenant.baseURL}/background.png')` }} />
        <div className="background-shadow" />
        <div className="content top-bar">
          <form className="form" onSubmit={handleSubmit(onSubmit)}>
            <IonGrid>
              {(!passwordChangeCalled || passwordChangeLoading) && (
                <>
                  <IonRow>
                    <IonCol size="12">
                      <IonItem className={errors.newPassword ? "error" : ""}>
                        <IonLabel position="floating">{t("auth.new_password")}</IonLabel>
                        <Controller name="newPassword" control={control} render={({ field }) => <IonInput type="password" onIonChange={field.onChange} onIonBlur={field.onBlur} />} />
                      </IonItem>
                      {errors.newPassword && <IonNote color="danger">{errors.newPassword?.message}</IonNote>}
                    </IonCol>
                    <IonCol size="12">
                      <IonItem className={errors.confirmPassword ? "error" : ""}>
                        <IonLabel position="floating">{t("auth.confirm_password")}</IonLabel>
                        <Controller name="confirmPassword" control={control} render={({ field }) => <IonInput type="password" onIonChange={field.onChange} onIonBlur={field.onBlur} />} />
                      </IonItem>
                      {errors.confirmPassword && <IonNote color="danger">{errors.confirmPassword?.message}</IonNote>}
                    </IonCol>
                  </IonRow>
                  <IonRow className="form-buttons">
                    <IonCol size="12">
                      <IonButton type="submit" color="primary">
                        {t("auth.change_password")}
                      </IonButton>
                    </IonCol>
                  </IonRow>
                </>
              )}
              {passwordChangeCalled && !passwordChangeLoading && (
                <IonRow className="">
                  <IonCol size="12">
                    <IonCard className="result-card">
                      <IonGrid>
                        <IonRow>
                          <IonCol size="2">
                            <IonIcon
                              className="icon"
                              icon={!passwordChangeData ? warning : informationCircle}
                              color={!passwordChangeData ? "warning" : "success"}
                            />
                          </IonCol>
                          <IonCol size="10">
                            <IonCardHeader>
                              <IonCardTitle>{t(!passwordChangeData ? "auth.password_change_error" : (isWebOrigin ? "auth.password_change_success": "auth.password_change_success_app"))}</IonCardTitle>
                            </IonCardHeader>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCard>
                  </IonCol>
                </IonRow>
              )}
            </IonGrid>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PasswordRecovery;
